import { Attr } from '@mode-switch/model';
import { Encoding, VariableType } from '@mode-vegas/vegas';

import { ChartTypes, FlamingoTypes, ChartRenderTypes } from '@mode/shared/contract-common';
import { makeSeriesNameFormatter, makeXValueFormatter, RenderDataModel } from '@mode/charts/util-vegas';

const MAX_POINTS = 25000;
const MAX_SERIES = 2500;
const MAX_SLICES = 1000; // Highcharts limit
const MAX_FACETS = 250;

export enum ChartDataErrorReason {
  TooManyPoints,
  TooManySeries,
  TooManySlices,
  TooManyFacets,
}

export class ChartDataError extends Error {
  constructor(
    public reason: ChartDataErrorReason,
    public count: number,
    public seriesCount: number
  ) {
    super(`Too much data, series: ${seriesCount}, total: ${count}`);
    Object.setPrototypeOf(this, ChartDataError.prototype);
  }
}

export function getChartDataErrorMsg(err: Error) {
  if (err instanceof ChartDataError) {
    switch (err.reason) {
      case ChartDataErrorReason.TooManyPoints:
        return {
          renderError: `Chart data contains ${err.count} data points, but the limit is ${MAX_POINTS}`,
          renderErrorHelp:
            'Displaying this chart could hang your browser. Try adding filters to reduce the number of data points.',
        };
      case ChartDataErrorReason.TooManySeries:
        return {
          renderError: `Chart data contains ${err.seriesCount} series, but the limit is ${MAX_SERIES}`,
          renderErrorHelp: 'Displaying this chart could hang your browser. Try adding a filter on the color field.',
        };
      case ChartDataErrorReason.TooManySlices:
        return {
          renderError: `Chart data contains ${err.seriesCount} series, but the limit is ${MAX_SLICES}`,
          renderErrorHelp: 'Displaying this chart could hang your browser. Try adding a filter on the color field.',
        };
      case ChartDataErrorReason.TooManyFacets:
        return {
          renderError: `Chart data contains ${err.count} facets, but the limit is ${MAX_FACETS}`,
          renderErrorHelp:
            'Displaying this chart could hang your browser. Try adding a filter on discrete fields in rows and columns.',
        };
    }
  } else {
    return {
      renderError: 'Unknown error',
      renderErrorHelp: err.message,
    };
  }
}

export namespace ChartData {
  function checkDataSizes(series: Array<{ name: any; data: any[] }>) {
    const totalCount = series.reduce((count, s) => count + s.data.length, 0);

    if (series.length > MAX_SERIES) {
      throw new ChartDataError(ChartDataErrorReason.TooManySeries, totalCount, series.length);
    }

    if (totalCount > MAX_POINTS) {
      throw new ChartDataError(ChartDataErrorReason.TooManyPoints, totalCount, series.length);
    }
  }

  export function makeLineData(
    model: RenderDataModel,
    encoding: Encoding,
    seriesField: FlamingoTypes.ModeField,
    xField: FlamingoTypes.ModeField,
    yField: FlamingoTypes.ModeField,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    switch (xField.type) {
      case Attr.Type.Discrete:
        return makeDiscreteLineData(model, encoding, seriesField, xField, yField, chartFormat);
      case Attr.Type.Continuous:
        return makeContinuousLineData(model, encoding, seriesField, xField, yField, chartFormat);
    }
  }

  export function makeDiscreteLineData(
    model: RenderDataModel,
    encoding: Encoding,
    seriesField: FlamingoTypes.ModeField,
    xField: FlamingoTypes.ModeField,
    yField: FlamingoTypes.ModeField,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    const categories = new Set<any>();
    const seriesByKey = new Map<string, any[]>();
    const categoriesByKey = new Map<string, Set<any>>();
    const dataset = model.getSeriesData(yField.alias);

    const xFieldFormatter = makeXValueFormatter(model.compileField(xField));
    const seriesNameFormatter = makeSeriesNameFormatter(model.compileField(seriesField), chartFormat);

    dataset.forEach((datum: any, index: number) => {
      const seriesKey = seriesNameFormatter(datum[seriesField.alias]);
      let seriesValues = seriesByKey.get(seriesKey);

      if (seriesValues === undefined) {
        seriesValues = new Array<any>();
        seriesByKey.set(seriesKey, seriesValues);
      }

      let seriesCategories = categoriesByKey.get(seriesKey);

      if (seriesCategories === undefined) {
        seriesCategories = new Set<any>();
        categoriesByKey.set(seriesKey, seriesCategories);
      }

      const xValue = xFieldFormatter(datum[xField.alias]);

      categories.add(xValue);
      seriesCategories.add(xValue);
      seriesValues.push(datum[yField.alias]);
    });

    const series = new Array<ChartRenderTypes.LineSeries>();
    const seriesKeys = Array.from(seriesByKey.keys());

    for (const name of seriesKeys) {
      const data = seriesByKey.get(name);

      const seriesCategories = categoriesByKey.get(name);
      Array.from(categories).forEach((category, index) => {
        if (!seriesCategories.has(category)) {
          data.splice(index, 0, null);
        }
      });

      series.push({ name, data });
    }

    checkDataSizes(series);

    return {
      series,
      seriesType: ChartRenderTypes.SeriesType.LINE,
      categories: Array.from(categories),
    };
  }

  export function makeContinuousLineData(
    model: RenderDataModel,
    encoding: Encoding,
    seriesField: FlamingoTypes.ModeField,
    xField: FlamingoTypes.ModeField,
    yField: FlamingoTypes.ModeField,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    const seriesByKey = new Map<string, any[]>();
    const dataset = model.getSeriesData(yField.alias);

    const xFieldFormatter = makeXValueFormatter(model.compileField(xField));
    const seriesNameFormatter = makeSeriesNameFormatter(model.compileField(seriesField), chartFormat);

    dataset.forEach((datum: any, index: number) => {
      const seriesKey = seriesNameFormatter(datum[seriesField.alias]);
      let seriesValues = seriesByKey.get(seriesKey);

      if (seriesValues === undefined) {
        seriesValues = new Array<any>();
        seriesByKey.set(seriesKey, seriesValues);
      }

      const xValue = xFieldFormatter(datum[xField.alias]);
      seriesValues.push([xValue, datum[yField.alias]]);
    });

    const series = new Array<ChartRenderTypes.LineSeries>();
    seriesByKey.forEach((data, name) => {
      series.push({ name, data });
    });

    checkDataSizes(series);

    return {
      series,
      seriesType: ChartRenderTypes.SeriesType.LINE,
    };
  }

  export function makeComboData(
    model: RenderDataModel,
    encoding: Encoding,
    xField: FlamingoTypes.ModeField,
    seriesFields: ChartRenderTypes.SeriesField[],
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    switch (xField.type) {
      case Attr.Type.Discrete:
        return makeDiscreteComboData(model, encoding, xField, seriesFields, chartFormat);
      case Attr.Type.Continuous:
        return makeContinuousComboData(model, encoding, xField, seriesFields, chartFormat);
    }
  }

  export function makeDiscreteComboData(
    model: RenderDataModel,
    encoding: Encoding,
    xAxisField: FlamingoTypes.ModeField,
    seriesFields: ChartRenderTypes.SeriesField[],
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    const categories = new Set<any>();
    const series = new Array<ChartRenderTypes.LineSeries>();

    let seriesData: ChartRenderTypes.LineData;
    seriesFields.forEach((seriesField) => {
      seriesData = makeDiscreteLineData(
        model,
        encoding,
        seriesField.groupField,
        xAxisField,
        seriesField.yField,
        chartFormat
      );

      seriesData.series.forEach((lineSeries) => {
        series.push({
          type: seriesField.type,
          name: seriesField.name,
          yAxis: seriesField.yAxis,
          data: lineSeries.data,
        });
      });

      for (const category of Array.from(seriesData.categories)) {
        categories.add(category);
      }
    });

    checkDataSizes(series);

    return {
      series,
      seriesType: ChartRenderTypes.SeriesType.LINE,
      categories: Array.from(categories),
    };
  }

  export function makeContinuousComboData(
    model: RenderDataModel,
    encoding: Encoding,
    xAxisField: FlamingoTypes.ModeField,
    seriesFields: ChartRenderTypes.SeriesField[],
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    const series = new Array<ChartRenderTypes.LineSeries>();

    let seriesData: ChartRenderTypes.LineData;
    seriesFields.forEach((seriesField) => {
      seriesData = makeContinuousLineData(
        model,
        encoding,
        seriesField.groupField,
        xAxisField,
        seriesField.yField,
        chartFormat
      );

      seriesData.series.forEach((lineSeries) => {
        series.push({
          type: seriesField.type,
          name: seriesField.name,
          yAxis: seriesField.yAxis,
          data: lineSeries.data,
        });
      });
    });

    checkDataSizes(series);

    return {
      series,
      seriesType: ChartRenderTypes.SeriesType.LINE,
    };
  }

  export function makeBubbleData(
    model: RenderDataModel,
    encoding: Encoding,
    colorField: FlamingoTypes.ModeField,
    xField: FlamingoTypes.ModeField,
    yField: FlamingoTypes.ModeField,
    zField: FlamingoTypes.ModeField,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.BubbleData {
    switch (xField.type) {
      case Attr.Type.Discrete:
        return makeDiscreteBubbleData(model, encoding, colorField, xField, yField, zField, chartFormat);
      case Attr.Type.Continuous:
        return makeContinuousBubbleData(model, encoding, colorField, xField, yField, zField, chartFormat);
    }
  }

  export function makeDiscreteBubbleData(
    model: RenderDataModel,
    encoding: Encoding,
    colorField: FlamingoTypes.ModeField,
    xField: FlamingoTypes.ModeField,
    yField: FlamingoTypes.ModeField,
    zField: FlamingoTypes.ModeField,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.BubbleData {
    const categories = new Set<any>();
    const seriesByKey = new Map<string, ChartRenderTypes.NamedPoint[]>();
    const dataset = model.getSeriesData(yField.alias);

    const xFieldFormatter = makeXValueFormatter(model.compileField(xField));
    const seriesNameFormatter = makeSeriesNameFormatter(model.compileField(colorField), chartFormat);

    dataset.forEach((datum: any, index: number) => {
      const seriesKey = seriesNameFormatter(datum[colorField.alias]);
      let seriesValues = seriesByKey.get(seriesKey);

      if (seriesValues === undefined) {
        seriesValues = new Array<ChartRenderTypes.NamedPoint>();
        seriesByKey.set(seriesKey, seriesValues);
      }

      const xValue = xFieldFormatter(datum[xField.alias]);

      categories.add(xValue);
      seriesValues.push({ y: datum[yField.alias], z: datum[zField.alias], name: xValue });
    });

    const series = new Array<ChartRenderTypes.LineSeries>();
    const seriesKeys = Array.from(seriesByKey.keys());

    for (const name of seriesKeys) {
      const data = seriesByKey.get(name);

      series.push({ name, data });
    }

    checkDataSizes(series);

    return {
      series,
      seriesType: ChartRenderTypes.SeriesType.BUBBLE,
      categories: Array.from(categories),
    };
  }

  export function makeContinuousBubbleData(
    model: RenderDataModel,
    encoding: Encoding,
    colorField: FlamingoTypes.ModeField,
    xField: FlamingoTypes.ModeField,
    yField: FlamingoTypes.ModeField,
    zField: FlamingoTypes.ModeField,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.BubbleData {
    const seriesByKey = new Map<string, ChartRenderTypes.NamedPoint[]>();
    const dataset = model.getSeriesData(yField.alias);

    const xFieldFormatter = makeXValueFormatter(model.compileField(xField));
    const seriesNameFormatter = makeSeriesNameFormatter(model.compileField(colorField), chartFormat);

    dataset.forEach((datum: any, index: number) => {
      const seriesKey = seriesNameFormatter(datum[colorField.alias]);
      let seriesValues = seriesByKey.get(seriesKey);

      if (seriesValues === undefined) {
        seriesValues = new Array<ChartRenderTypes.NamedPoint>();
        seriesByKey.set(seriesKey, seriesValues);
      }

      const xValue = xFieldFormatter(datum[xField.alias]);
      seriesValues.push({ y: datum[yField.alias], z: datum[zField.alias], x: xValue });
    });

    const series = new Array<ChartRenderTypes.LineSeries>();
    seriesByKey.forEach((data, name) => {
      series.push({ name, data });
    });

    checkDataSizes(series);

    return {
      series,
      seriesType: ChartRenderTypes.SeriesType.BUBBLE,
    };
  }

  export function makeNamedPointData(
    model: RenderDataModel,
    encoding: Encoding,
    xField: FlamingoTypes.ModeField,
    yField: FlamingoTypes.ModeField,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.PointData {
    const points = new Array<ChartRenderTypes.NamedPoint>();
    const data = model.getSeriesData('*');

    const xFieldFormatter = makeSeriesNameFormatter(model.compileField(xField), chartFormat);

    data.forEach((datum: any, index: number) => {
      const xValue = xFieldFormatter(datum[xField.alias]);

      points.push({
        name: xValue,
        y: datum[yField.alias],
      });
    });

    const series = [
      {
        name: yField.name,
        data: points,
        colorByPoint: true,
      },
    ];

    // Highcharts default limit on pie chart is 1000 slices
    if (series[0].data.length > 1000) {
      throw new ChartDataError(ChartDataErrorReason.TooManySlices, series[0].data.length, series[0].data.length);
    }

    return {
      series,
      seriesType: ChartRenderTypes.SeriesType.NAMED_POINT,
    };
  }

  export function makePairedPointData(
    model: RenderDataModel,
    encoding: Encoding,
    xField: FlamingoTypes.ModeField,
    yField: FlamingoTypes.ModeField,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.PairedPointData {
    const categories = new Set<any>();
    const points = new Array<ChartRenderTypes.PairedPoint>();
    const data = model.getSeriesData('*');

    const xFieldFormatter = makeSeriesNameFormatter(model.compileField(xField), chartFormat);

    data.forEach((datum: any, index: number) => {
      const xValue = xFieldFormatter(datum[xField.alias]);

      categories.add(xValue);
      points.push([xValue, datum[yField.alias]]);
    });

    const series = [
      {
        name: yField.name,
        data: points,
      },
    ];

    // Highcharts default limit on pie chart is 1000 slices
    if (series[0].data.length > 1000) {
      throw new ChartDataError(ChartDataErrorReason.TooManySlices, series[0].data.length, series[0].data.length);
    }

    return {
      series,
      seriesType: ChartRenderTypes.SeriesType.PAIRED_POINT,
      categories: Array.from(categories),
    };
  }

  /**
   * Transformations
   */

  export function transformNullstoZero(
    chartData: ChartRenderTypes.LineData,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.LineData {
    if (chartFormat.nullToZero) {
      const series = new Array<ChartRenderTypes.LineSeries>();
      chartData.series.forEach((chartSeries, index) => {
        series.push({
          ...chartSeries,
          data: chartSeries.data.map((datum) => datum || 0),
        });
      });
      return { ...chartData, series };
    } else {
      return chartData;
    }
  }

  export function transformOrphanPoints(
    varType: VariableType
  ): (chartData: ChartRenderTypes.LineData, chartFormat: ChartTypes.ChartFormat) => ChartRenderTypes.LineData {
    if (varType === VariableType.DISCRETE) {
      // show markers for "orphan" data points that don't have an
      // immediate predecessor and successor
      return (chartData: ChartRenderTypes.LineData, chartFormat: ChartTypes.ChartFormat) => ({
        ...chartData,
        series: chartData.series.map((series) => {
          const rawData: number[] = series.data;
          const data = rawData.map((y, i) => {
            const noPrev = i === 0 || rawData[i - 1] === null;
            const noNext = i === rawData.length - 1 || rawData[i + 1] === null;
            if (noPrev && noNext) {
              return { y, marker: { radius: 4 } };
            } else {
              return y;
            }
          });
          return { ...series, data };
        }),
      });
    } else {
      // show markers for "orphan" data points that have no siblings
      return (chartData: ChartRenderTypes.LineData, chartFormat: ChartTypes.ChartFormat) => ({
        ...chartData,
        series: chartData.series.map((series) => {
          const rawData = series.data;
          let data: number[][] | { x: number; y: number; marker: { radius: number } }[] = rawData;
          if (rawData.length === 1) {
            const [[x, y]] = rawData;
            data = [{ x, y, marker: { radius: 4 } }];
          }
          return { ...series, data };
        }),
      });
    }
  }

  export function transformPointInnerSize(
    chartData: ChartRenderTypes.PointData,
    chartFormat: ChartTypes.ChartFormat
  ): ChartRenderTypes.PointData {
    switch (chartData.seriesType) {
      case ChartRenderTypes.SeriesType.PAIRED_POINT:
        const series = new Array<ChartRenderTypes.PairedPointSeries>();
        chartData.series.forEach((chartSeries, index) => {
          series.push({
            ...chartSeries,
            innerSize: '50%',
          });
        });
        return { ...chartData, series };
      default:
        return chartData;
    }
  }
}
